import deepmerge from 'deepmerge';
import type { Config, DeepPartial } from '../default/types';
import defaultConfig, { customArrayMerge, environment, getFullLocaleFromConfig } from '../default';
import { productConfig } from './product/product';
import { installmentCalculatorConfig } from './product/installmentCalculator';
import { dynamicHelp } from './cms/dynamicHelp';

const tenantId = 'ackermannch';

const config = deepmerge<Config, DeepPartial<Config>>(
  defaultConfig,
  {
    host: { name: tenantId, domain: 'ackermann.ch', appDeeplinkProtocol: 'achapp' },
    clientId: 'AckermannCh',
    i18n: {
      defaultLocale: 'de-CH',
      locales: ['de-CH', 'fr-CH'],
      languages: ['de', 'fr'],
    },
    tenantId,
    company: {
      name: 'Ackermann',
      seoTitleName: 'Ackermann.ch',
    },
    timeZone: 'Europe/Zurich',
    headerSlots: {
      localeSwitcher: true,
      serviceApi: `https://storage.googleapis.com/drupal-pages/${environment}/menus/${tenantId}/<locale>/bucket-service-flyout.json`,
    },
    assets: {
      cmsImage: {
        imageOptimizationEnabled: true,
        uriTransformEndpoint: `https://storage.googleapis.com/drupal-images-${tenantId}/$1/`,
      },
      assetServerHost: 'https://bilder.ackermann.ch',
    },
    staticContent: {
      apiEndpoints: {
        webappJson: `http://cloud-service-cms.${tenantId}.svc.cluster.local:3100`,
        bucket: `https://storage.googleapis.com/drupal-pages/${environment}<subfolder>/${tenantId}/<locale>/<identifier>.json`,
      },
    },
    product: productConfig,
    installmentCalculator: installmentCalculatorConfig,
    search: {
      adServer: {
        mode: 'containerOnly',
      },
    },
    middleware: {
      cmsRewrites: {
        exact: ['/', '/storefront', '/fr', '/mafo/unsubscribe', '/uwg7/unsubscribe'],
        prefixes: [
          '/service-hilfe',
          '/aide-service',
          '/lp',
          '/magazin',
          '/magazine',
          '/node',
          '/servicelayer/',
          '/preview-app/',
          '/mailings/',
        ],
      },
    },
    order: {
      seals: {},
      assets: {
        premium: {
          getPremiumImage: ({ language }: { language: string }) =>
            `https://bilder.ackermann.ch/empiriecom/io/ackermannch/${language}/Header_Service_Vorteile_Treuepaeckli.svg`,
          getPremiumIcon: () =>
            'https://bilder.ackermann.ch/empiriecom/io/ackermannch/de/premiumicon.svg',
        },
        gifs: {
          success: {
            url: 'https://bilder.ackermann.ch/i/empiriecom/io/de/thank_you_1_transparent.gif',
          },
        },
      },
      accountInformation: {
        bicAvailable: false,
      },
      addressValidation: {
        zipCode: {
          length: 4,
        },
      },
    },
    footer: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/drupal-pages/${environment}<subfolder>/${tenantId}/<locale>/<identifier>.json`,
      },
    },
    forms: {
      newsletterSettings: {
        useDOI: false,
        sendAdditionalSubscribe: true,
      },
      contact: {
        dynamicHelp,
      },
      newsletterUnsubscribe: {
        useDedicatedSubmitButtonTitleFor: ['unsubscribe|99'],
        fields: [
          {
            type: 'newsletter',
            formType: 'radio',
            defaultValue: 'unsubscribe|99',
            options: [
              {
                id: 'frq-daily',
                value: 'frequency|0',
                messageKey: 'forms.newsletter.unsubscribe.type.frequency.option.daily',
              },
              {
                id: 'frq-weekly',
                value: 'frequency|1',
                messageKey: 'forms.newsletter.unsubscribe.type.frequency.option.weekly',
              },
              {
                id: 'unsubscribe',
                value: 'unsubscribe|99',
                messageKey: 'forms.newsletter.unsubscribe.type.unsubscribe',
              },
              {
                id: 'pause',
                value: 'pause|-100',
                messageKey: 'forms.newsletter.unsubscribe.type.pause',
              },
            ],
            additionalForm: {
              id: 'pause',
              formType: 'select',
              showOnlyWhenWhatSelected: 'pause',
              messageKeyPlaceholder: 'forms.newsletter.unsubscribe.type.pause.label',
              options: [
                {
                  id: 'pause-7',
                  value: '7',
                  messageKey: 'forms.newsletter.unsubscribe.type.pause.option.7',
                },
                {
                  id: 'pause-14',
                  value: '14',
                  messageKey: 'forms.newsletter.unsubscribe.type.pause.option.14',
                },
                {
                  id: 'pause-21',
                  value: '21',
                  messageKey: 'forms.newsletter.unsubscribe.type.pause.option.21',
                },
                {
                  id: 'pause-30',
                  value: '30',
                  messageKey: 'forms.newsletter.unsubscribe.type.pause.option.30',
                },
              ],
            },
          },
        ],
      },
      newsletterSubscribe: {
        de: {
          unsubscribeLink: 'https://www.ackermann.ch/service-hilfe/vorteile/newsletter-abmelden',
          voucherLink: 'https://www.ackermann.ch/service-hilfe/vorteile/gutscheine-rabatte/',
        },
        fr: {
          unsubscribeLink:
            'https://www.ackermann.ch/fr/aide-service/avantages/desinscription-de-la-newsletter',
          voucherLink: 'https://www.ackermann.ch/fr/aide-service/avantages/bons-rabais/',
        },
      },
      braFinder: {
        de: { searchLink: '/s/CUP+{cupSize}%2C+Grösse+{size}' },
      },
      validation: {
        rules: {
          fileUpload: {
            maxFileSizeMb: 20,
            maxFileSizeSummaryMb: 20,
            acceptedFormats: 'image/*,.heic,.heif,.mov',
            regexAcceptedUploadFormats: /\.(jpe?g|png|gif|webp|bmp|heic|heif|mov)$/i,
          },
        },
      },
    },
    banner: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/drupal-contentsnippet/${environment}/${tenantId}/<locale>/<identifier>.json`,
      },
    },
    nlSheetTexts: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/drupal-contentsnippet/${environment}/${tenantId}/<locale>/nl-sheet.json`,
      },
    },
    serviceLayer: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/drupal-contentsnippet/${environment}/${tenantId}/<locale>/<identifier>.json`,
      },
    },
    seoContent: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/inspire-seofiles/<locale>_${tenantId}-<category>.json`,
      },
      url: {
        'de-CH': {
          privacyNoticePath: '/service-hilfe/ueber-uns/datenschutz',
          potentialActionTarget: 'https://www.ackermann.ch/s/',
        },
        'fr-CH': {
          privacyNoticePath: '/fr/aide-service/qui-sommes-nous/protection-des-donnees',
          potentialActionTarget: 'https://www.ackermann.ch/s/',
        },
      },
    },
    survey: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/drupal-surveys/${environment}/${tenantId}/<locale>/<identifier>.json`,
      },
    },
    promotionBanner: {
      apiEndpoints: {
        bucketEmptyPromotions: `https://storage.googleapis.com/drupal-contentsnippet/${environment}/${tenantId}/<language>/empty-promotion.json`,
        bucketPromotion: `https://storage.googleapis.com/promotion-data/${tenantId}/<language>/<code>.json`,
        bucketContentSnippet: `https://storage.googleapis.com/drupal-contentsnippet/${environment}/${tenantId}/<language>/<identifier>.json`,
      },
    },
    voucher: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/promotion-data/${tenantId}/<locale>/<identifier>.json`,
      },
    },
    appBanner: {
      appUrl: 'https://app.adjust.com/3fy9f3p_t6shmz5',
    },
    tracking: {
      categoryIdHomepage: '124601',
      soastaId: 'QB55A-QDUKY-23T2L-SWCLT-57YSK',
      tagmanagerId: 'GTM-MZZBD9K',
      googleSiteVerificationID: ['-KF2ltJciuiR-I1bRrpPYNZwB6h0nZji50jQpYgroRc'],
      msvalidateID: '4067315BE5669CD6C74BDEC6F6AC5D47',
      pinterestRichPinVerificationID: 'add46bfb6e0cf176e43e61b50007f9f6',
      schemaOrgOrganisation: {
        'de-CH': {
          '@context': 'https://schema.org',
          '@type': 'Organization',
          '@id': 'https://www.ackermann.ch/',
          name: 'Ackermann',
          alternateName: 'Ackermann Versand',
          description:
            'Ackermann ist ein Schweizer Online-Shop, der Mode, Möbel und Technik anbietet. Ackermann ist bekannt für seine breite Produktauswahl und seinen bequemen und sicheren Online-Einkauf. Ackermann ist *nicht* nur ein reiner Online-Händler, sondern ein Anbieter für ein umfassendes Einkaufserlebnis in der Schweiz.',
          url: 'https://www.ackermann.ch/',
          logo: 'https://www.ackermann.ch/assets/logos/ackermannch/logo.svg',
          foundingDate: '1842-01-01',
          founder: {
            '@type': 'Person',
            name: 'Johann Ackermann',
            description: 'Gründer von Ackermann',
          },
          location: {
            '@type': 'Place',
            name: 'Otelfingen',
            address: {
              '@type': 'PostalAddress',
              streetAddress: 'Industriestrasse 19',
              addressLocality: 'Otelfingen',
              addressRegion: 'ZH',
              postalCode: '8112',
              addressCountry: 'CH',
            },
          },
          contactPoint: [
            {
              '@type': 'ContactPoint',
              contactType: 'customer service',
              telephone: '+41-848-85-85-11',
              email: 'service@ackermann.ch',
              availableLanguage: ['de'],
              hoursAvailable: ['Mo-Su 07:00-22:00'],
              url: 'https://www.ackermann.ch/service-hilfe/ueber-uns/kontakt',
            },
          ],
          sameAs: [
            'https://www.facebook.com/ackermannversand',
            'https://www.youtube.com/user/ackermannversandCH',
            'https://de.wikipedia.org/wiki/Ackermann_(Versand)',
          ],
          legalName: 'Ackermann Vertriebs AG',
          areaServed: 'CH',
          memberOf: {
            '@type': 'Organization',
            name: 'Otto Group',
            sameAs: 'https://www.ottogroup.com/',
          },
          foundingLocation: 'Entlebuch',
          slogan: 'Ackermann - das echte Leben.',
        },
        'fr-CH': {
          '@context': 'https://schema.org',
          '@type': 'Organization',
          '@id': 'https://www.ackermann.ch/fr/',
          name: 'Ackermann',
          alternateName: 'Ackermann Versand',
          description:
            "Ackermann est une boutique en ligne suisse proposant de la mode, des meubles et de la technique. Ackermann est connu pour son large choix de produits et son expérience d'achat en ligne pratique et sécurisée. Ackermann n'est *pas* seulement un simple détaillant en ligne, mais un fournisseur d'une expérience d'achat complète en Suisse.",
          url: 'https://www.ackermann.ch/fr/',
          logo: 'https://www.ackermann.ch/assets/logos/ackermannch/logo.svg',
          foundingDate: '1842-01-01',
          founder: {
            '@type': 'Person',
            name: 'Johann Ackermann',
            description: "Fondateur d'Ackermann",
          },
          location: {
            '@type': 'Place',
            name: 'Otelfingen',
            address: {
              '@type': 'PostalAddress',
              streetAddress: 'Industriestrasse 19',
              addressLocality: 'Otelfingen',
              addressRegion: 'ZH',
              postalCode: '8112',
              addressCountry: 'CH',
            },
          },
          contactPoint: [
            {
              '@type': 'ContactPoint',
              contactType: 'customer service',
              telephone: '+41-848-85-85-11',
              email: 'service@ackermann.ch',
              availableLanguage: ['de', 'fr'],
              hoursAvailable: ['Mo-Su 07:00-22:00'],
              url: 'https://www.ackermann.ch/fr/aide-service/qui-sommes-nous/contactez-nous',
            },
          ],
          sameAs: [
            'https://www.facebook.com/ackermannversand',
            'https://www.youtube.com/user/ackermannversandCH',
            'https://de.wikipedia.org/wiki/Ackermann_(Versand)',
          ],
          legalName: 'Ackermann Vertriebs AG',
          areaServed: 'CH',
          memberOf: {
            '@type': 'Organization',
            name: 'Otto Group',
            sameAs: 'https://www.ottogroup.com/',
          },
          foundingLocation: 'Entlebuch',
          slogan: 'Ackermann - das echte Leben.',
        },
      },
    },
    externalScript: {
      kuecheCo: {
        key: 'pAYrueynYRcqna7lGLoVT39kF83Wy3PYENp81Yw0QJqeTyzAjXvc1FwGOzCm',
      },
    },
    headerInformation: {
      de: {
        description:
          'Ackermann - Schweizer Online Shop für Mode, Möbel u. Technik | Gratis 3 Jahre Garantie ✓ Gratis Versand ab CHF 99.- ✓ auf Rechnung und in Raten bestellen',
        title: 'Ackermann Versand Schweiz | Mode - Möbel - Technik online kaufen',
        canonical: 'https://www.ackermann.ch',
        robots: 'index,follow',
      },
      fr: {
        description:
          "Ackermann VPC - Votre boutique de mode, vêtements, chaussures et meubles pour vos achats en ligne à prix avantageux. Commander 24h/24, à l'aise depuis chez soi, ...",
        title: 'Boutique mode | Vêtements | Meubles acheter Ackermann VPC',
        canonical: 'https://www.ackermann.ch',
        robots: 'index,follow',
      },
    },
    // kim is renamed as "customerCentricFeedback"
    customerCentricFeedback: {
      tenantId: '1400',
    },
    dynamicYieldTracking: {
      key: '656c2ade1a744c7b932774a6b8c7012ac01aa46358d787f92bfd2058a97870a5',
    },
    geoCountryLayer: {
      enabled: true,
      flag: 'ch',
      alternatives: [
        {
          flag: 'de',
          shops: [
            {
              link: 'https://www.baur.de',
            },
          ],
        },
        {
          flag: 'at',
          shops: [
            {
              link: 'https://www.universal.at',
            },
          ],
        },
      ],
    },
    priceFormat: 'NoDelimiterCurrencyAtTheEnd',
  },
  { arrayMerge: customArrayMerge },
);

export const getFullLocale = (localeOrLanguage: string | undefined) =>
  getFullLocaleFromConfig(localeOrLanguage, config);

// eslint-disable-next-line import-x/no-default-export
export default config;
